/**
 * Global Modal System - JavaScript API
 * 
 * A reusable modal system that can be triggered from anywhere in the theme.
 * 
 * USAGE:
 * ------
 * 
 * 1. Simple usage (string content, default settings):
 *    document.dispatchEvent(new CustomEvent('modal:open', {
 *      detail: '<h2>Your Content</h2><p>Modal content here...</p>'
 *    }));
 * 
 * 2. With options object:
 *    document.dispatchEvent(new CustomEvent('modal:open', {
 *      detail: {
 *        content: '<h2>Your Content</h2>',           // String or HTMLElement
 *        animation: 'appear',                         // 'appear', 'slide-top', 'slide-bottom', 'slide-left', 'slide-right'
 *        showCloseButton: true,                       // true or false
 *        closeButtonPosition: { top: 12, right: 12 }  // { top, right, bottom, left } in pixels
 *      }
 *    }));
 * 
 * 3. Animation options:
 *    - 'appear' - Fade in with scale (no slide)
 *    - 'slide-top' - Slide down from top (default)
 *    - 'slide-bottom' - Slide up from bottom
 *    - 'slide-left' - Slide in from left
 *    - 'slide-right' - Slide in from right
 * 
 * 4. Close button positioning examples:
 *    { top: 20, right: 20 }     // Top-right corner
 *    { top: 12, left: 12 }      // Top-left corner
 *    { bottom: 20, right: 20 }  // Bottom-right corner
 *    { bottom: 12, left: 12 }   // Bottom-left corner
 * 
 * 5. Close modal programmatically:
 *    document.dispatchEvent(new CustomEvent('modal:close'));
 * 
 * 6. Listen to modal events:
 *    document.addEventListener('modal:opened', () => {
 *      console.log('Modal opened');
 *    });
 *    document.addEventListener('modal:closed', () => {
 *      console.log('Modal closed');
 *    });
 * 
 * 7. Direct API access:
 *    window.modalGlobal.open({ content: '<p>Hello</p>' });
 *    window.modalGlobal.close();
 * 
 * EXAMPLES:
 * ---------
 * 
 * Example 1 - Simple modal:
 * document.dispatchEvent(new CustomEvent('modal:open', {
 *   detail: {
 *     content: '<h2>Welcome!</h2><p>This is a simple modal.</p>',
 *     animation: 'appear'
 *   }
 * }));
 * 
 * Example 2 - Modal without close button:
 * document.dispatchEvent(new CustomEvent('modal:open', {
 *   detail: {
 *     content: '<h2>Loading...</h2>',
 *     showCloseButton: false,
 *     animation: 'slide-bottom'
 *   }
 * }));
 * 
 * Example 3 - Custom close button position:
 * document.dispatchEvent(new CustomEvent('modal:open', {
 *   detail: {
 *     content: '<h2>Custom Position</h2>',
 *     animation: 'slide-right',
 *     closeButtonPosition: { top: 20, left: 20 }
 *   }
 * }));
 */

(function() {
  // Global Modal Controller
  const modalGlobal = {
    modal: null,
    overlay: null,
    body: null,
    header: null,
    closeButton: null,
    isOpen: false,

    init() {
      this.modal = document.querySelector('[data-modal-global]');
      this.overlay = document.querySelector('[data-modal-overlay]');
      this.body = document.querySelector('[data-modal-body]');
      this.wrapper = document.querySelector('[data-wrapper]');
      this.header = document.querySelector('[data-modal-header]');
      this.closeButton = document.querySelector('[data-modal-close]');

      if (!this.modal) return;

      this.bindEvents();
    },

    bindEvents() {
      // Close button click
      this.closeButton?.addEventListener('click', () => this.close());

      // Wrapper click to close (handles overlay clicks properly)
      this.wrapper?.addEventListener('click', (e) => {
        // Only close if clicking the wrapper itself, not its children
        if (e.target === this.wrapper) {
          this.close();
        }
      });

      // ESC key to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });

      // Global event listeners for opening modal
      document.addEventListener('modal:open', (e) => {
        this.open(e.detail);
      });

      document.addEventListener('modal:close', () => {
        this.close();
      });
    },

    open(options) {
      if (!this.modal || !this.body) return;

      // Handle both string/element content and options object
      let content, animation, showCloseButton, closeButtonPosition, endPosition;
      
      if (typeof options === 'string' || options instanceof HTMLElement) {
        // Backward compatibility: simple content passed directly
        content = options;
        animation = 'slide-to-top'; // default
        showCloseButton = true; // default
        closeButtonPosition = { top: 12, right: 12 }; // default
        endPosition = 'center'; // default
      } else if (options && typeof options === 'object') {
        // Options object with content, animation, and close button visibility
        content = options.content;
        animation = options.animation || 'slide-to-top';
        showCloseButton = options.showCloseButton !== undefined ? options.showCloseButton : true;
        closeButtonPosition = options.closeButtonPosition || { top: 12, right: 12 };
        endPosition = options.endPosition || 'center';
      }

      // Set content
      if (typeof content === 'string') {
        this.body.innerHTML = content;
      } else if (content instanceof HTMLElement) {
        this.body.innerHTML = '';
        this.body.appendChild(content);
      }

      // Set animation type
      this.modal.setAttribute('data-animation', animation);

      // Set end position for content alignment
      if (this.wrapper) {
        this.wrapper.setAttribute('data-end-position', endPosition);
      }

      // Show/hide close button and set position
      if (this.header) {
        if (showCloseButton) {
          this.header.classList.add('is-visible');
          
          // Reset all positions
          this.header.style.top = '';
          this.header.style.right = '';
          this.header.style.bottom = '';
          this.header.style.left = '';
          
          // Set provided positions
          if (closeButtonPosition.top !== undefined) {
            this.header.style.top = closeButtonPosition.top + 'px';
          }
          if (closeButtonPosition.right !== undefined) {
            this.header.style.right = closeButtonPosition.right + 'px';
          }
          if (closeButtonPosition.bottom !== undefined) {
            this.header.style.bottom = closeButtonPosition.bottom + 'px';
          }
          if (closeButtonPosition.left !== undefined) {
            this.header.style.left = closeButtonPosition.left + 'px';
          }
        } else {
          this.header.classList.remove('is-visible');
        }
      }

      // Show modal
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('modal-open');
      this.isOpen = true;

      // Dispatch opened event
      document.dispatchEvent(new CustomEvent('modal:opened'));
    },

    close() {
      if (!this.modal) return;

      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('modal-open');
      this.isOpen = false;

      // Dispatch closed event
      document.dispatchEvent(new CustomEvent('modal:closed'));

      // Clear content after animation (don't clear, just hide)
      // Content clearing was causing issues on re-opening
      // The content will be replaced on next open anyway
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => modalGlobal.init());
  } else {
    modalGlobal.init();
  }

  // Expose to window for direct access
  window.modalGlobal = modalGlobal;
})();
